import { createSlice, type PayloadAction } from '@reduxjs/toolkit'

/**
 * "Ask in chat" / "Reject" handoff from a work page (Tasks, Problems) into the
 * main chat. The page names the item; the chat rail opens with a chip for it
 * and — for a reject — marks the item rejected with the typed reason when the
 * message is sent. The page itself publishes the selected item's ids through
 * `useChatAppContext`, so the agent can act on it.
 */
export type ChatHandoffTone = 'reject' | 'ask'

export type WorkHandoffContext =
  | {
      source: 'occurrence'
      occurrenceId: string
      conceptId?: string
      taskId?: string
      runId?: string
      /** A debugging item the user is asking about, with what it points at. */
      debugItem?: {
        id: string
        kind: string
        title: string
        source: string
        target?: Record<string, string>
      }
    }
  | { source: 'legacy_result'; resultItemId: string; runId: string }

export interface ChatHandoff {
  context?: WorkHandoffContext
  /** Bumps on every handoff so the rail re-opens even for the same item. */
  id: number
  tone: ChatHandoffTone
  /** Chip text shown in the composer and on the sent message. */
  chipLabel: string
  /** Text prefilled in the composer (empty for a reject: the reason is required). */
  draft: string
  placeholder: string
  /** Result item ids to mark rejected, with the message as the reason, on send. */
  rejectItemIds: string[]
}

export interface ChatHandoffState {
  current: ChatHandoff | null
  seq: number
}

const initialState: ChatHandoffState = { current: null, seq: 0 }

const chatHandoffSlice = createSlice({
  name: 'chatHandoff',
  initialState,
  reducers: {
    openChatHandoff(state, action: PayloadAction<Omit<ChatHandoff, 'id'>>) {
      state.seq += 1
      state.current = { ...action.payload, id: state.seq }
    },
    clearChatHandoff(state) {
      state.current = null
    }
  }
})

interface ChatHandoffRootState {
  chatHandoff: ChatHandoffState
}

export const { openChatHandoff, clearChatHandoff } = chatHandoffSlice.actions

export function selectChatHandoff(state: ChatHandoffRootState): ChatHandoff | null {
  return state.chatHandoff.current
}

export default chatHandoffSlice.reducer
